import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle } from "lucide-react";

import { api } from "@/lib/api";
import { translateApiError } from "@/lib/form-errors";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
} from "@/components/ui/Card";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const mutation = useMutation({
    mutationFn: (body: { token: string }) => api.verifyEmail(body),
  });

  // Fire once on mount with the URL token. React StrictMode runs effects twice
  // in dev, so we guard with a ref to avoid double mutation.
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current || !token) return;
    firedRef.current = true;
    mutation.mutate({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // No token in URL
  if (!token) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("auth.email_verify.invalid_title")}</CardTitle>
          <CardSubtitle>{t("auth.email_verify.invalid_message")}</CardSubtitle>
        </CardHeader>
      </Card>
    );
  }

  // In-flight
  if (mutation.isPending || mutation.isIdle) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
              aria-hidden="true"
            />
          </div>
          <CardTitle>{t("auth.email_verify.verifying")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  // Error
  if (mutation.isError) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("auth.email_verify.invalid_title")}</CardTitle>
          <CardSubtitle>
            {translateApiError(t, mutation.error)}
          </CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link
            to="/login"
            className="text-caption font-medium text-brand-500 hover:underline"
          >
            {t("auth.forgot_password.back_to_login")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  // Success
  return (
    <Card>
      <CardHeader>
        <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
          <CheckCircle2 className="h-6 w-6 text-success-500" />
        </div>
        <CardTitle>{t("auth.email_verify.success_title")}</CardTitle>
        <CardSubtitle>
          {t("auth.email_verify.success_message", {
            email: mutation.data?.email,
          })}
        </CardSubtitle>
      </CardHeader>
      <CardBody>
        <Link
          to="/dashboard"
          className="inline-flex w-full items-center justify-center gap-2 rounded-pill bg-brand-500 px-6 py-3 text-body font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:shadow-focus"
        >
          {t("auth.email_verify.go_to_dashboard")}
        </Link>
      </CardBody>
    </Card>
  );
}
